import { useEffect, useRef, useState } from "react";
import { SignatureCanvas, type SignatureCanvasHandle } from "./SignatureCanvas";
import { TypedSignature } from "./TypedSignature";
import { signLeasePdf } from "../lib/leaseSigning";
import { formatDate } from "../lib/signaturePlacement";
import {
  formatUsPhoneInput,
  isValidUsPhone,
  normalizeUsPhone,
  US_PHONE_INVALID_MSG,
} from "../lib/phoneUs";
import type {
  ExhibitAPayload,
  KnownContact,
  OnboardingPrefill,
} from "../lib/api";

interface Props {
  leaseTemplateUrl: string;
  initialFullName?: string;
  prefill: OnboardingPrefill;
  onSigned: (out: {
    signedPdfBase64: string;
    signerFullName: string;
    exhibitA: ExhibitAPayload;
  }) => void;
}

type SignMode = "draw" | "type";

interface ContactDraft {
  name: string;
  phone: string;
  relationship: string;
}

interface FormState {
  fullName: string;
  dateOfBirth: string;
  addressLine: string;
  city: string;
  state: string;
  zipcode: string;
  phone: string;
  email: string;
  caseNumber: string;
  probationOfficerName: string;
  employerName: string;
  employerAddress: string;
  employerPhone: string;
  supervisorName: string;
  supervisorPhone: string;
  knownContacts: ContactDraft[];
  attestAccurate: boolean;
  agreeTerms: boolean;
}

const MAX_KNOWN_CONTACTS = 4;
const MIN_KNOWN_CONTACTS = 2;

const RELATIONSHIP_OPTIONS = [
  { value: "", label: "Select relationship" },
  { value: "Mother", label: "Mother" },
  { value: "Father", label: "Father" },
  { value: "Brother", label: "Brother" },
  { value: "Sister", label: "Sister" },
  { value: "Spouse/Partner", label: "Spouse / Partner" },
  { value: "Child", label: "Child" },
  { value: "Friend", label: "Friend" },
  { value: "Coworker", label: "Coworker" },
  { value: "Other", label: "Other" },
] as const;

function blankContact(): ContactDraft {
  return { name: "", phone: "", relationship: "" };
}

/**
 * Native `<input type="date">` needs and emits ISO `yyyy-mm-dd`, but the
 * lease PDF stamp + backend `parseDobToTimestamp` also accept `MM/DD/YYYY`.
 * These helpers convert both directions without introducing a TZ shift.
 */
function toDateInputValue(raw: string | null | undefined): string {
  if (!raw) return "";
  const t = raw.trim();
  if (!t) return "";
  // Already ISO?
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  const slash = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(t);
  if (slash) {
    const mm = slash[1]!.padStart(2, "0");
    const dd = slash[2]!.padStart(2, "0");
    const yyyy = slash[3]!;
    return `${yyyy}-${mm}-${dd}`;
  }
  return "";
}

function isoDobToDisplay(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  return `${m[2]}/${m[3]}/${m[1]}`;
}

/**
 * Validates a `yyyy-mm-dd` DOB from the native date input.
 * Returns null on valid, or an error string.
 */
function validateDob(iso: string): string | null {
  if (!iso) return "Date of birth is required.";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return "Enter your date of birth as MM/DD/YYYY.";
  const yyyy = Number(m[1]);
  const mm = Number(m[2]);
  const dd = Number(m[3]);
  if (yyyy < 1900) return "Date of birth year must be 1900 or later.";
  // Construct a UTC date and confirm the roundtrip matches (rejects e.g. Feb 30).
  const d = new Date(Date.UTC(yyyy, mm - 1, dd));
  if (
    d.getUTCFullYear() !== yyyy ||
    d.getUTCMonth() !== mm - 1 ||
    d.getUTCDate() !== dd
  ) {
    return "That date of birth doesn't look valid.";
  }
  // Reject a DOB in the future or today.
  const now = new Date();
  const todayUtc = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
  );
  if (d.getTime() >= todayUtc.getTime()) {
    return "Date of birth must be in the past.";
  }
  return null;
}

/** Today as `yyyy-mm-dd` (client local) for the date input's `max` attribute. */
function todayIso(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function buildInitial(
  prefill: OnboardingPrefill,
  initialFullName: string | undefined,
): FormState {
  // If a probation officer is already linked in Firestore we prefill from
  // it, but the field is still editable — the assigned officer can be wrong
  // or unset for brand-new clients.
  const linkedOfficer = prefill.probationOfficers[0];
  return {
    fullName: initialFullName || prefill.client.fullName || "",
    // Native <input type="date"> requires ISO yyyy-mm-dd. The backend
    // prefill returns MM/DD/YYYY (see formatDob in functions/onboarding.ts),
    // so normalize on load.
    dateOfBirth: toDateInputValue(prefill.client.dateOfBirth),
    addressLine: prefill.client.addressLine || "",
    city: prefill.client.city || "",
    state: prefill.client.state || "",
    zipcode: prefill.client.zipcode || "",
    phone: prefill.client.phone || "",
    email: prefill.client.email || "",
    caseNumber: "",
    probationOfficerName: linkedOfficer?.name ?? "",
    employerName: prefill.work.employerName || "",
    employerAddress: prefill.work.employerAddress || "",
    employerPhone: prefill.work.employerPhone || "",
    supervisorName: "",
    supervisorPhone: "",
    knownContacts: [blankContact(), blankContact()],
    attestAccurate: false,
    agreeTerms: false,
  };
}

function isFilled(value: string): boolean {
  return value.trim().length > 0;
}

export function SignStep({
  leaseTemplateUrl,
  initialFullName,
  prefill,
  onSigned,
}: Props) {
  const sigRef = useRef<SignatureCanvasHandle | null>(null);
  const [form, setForm] = useState<FormState>(() =>
    buildInitial(prefill, initialFullName),
  );
  const [signMode, setSignMode] = useState<SignMode>("draw");
  const [, forceTick] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const today = formatDate();

  // Re-issue a fresh ref between modes so the imperative handle attaches to
  // the currently-mounted child.
  useEffect(() => {
    sigRef.current = null;
  }, [signMode]);

  const linkedOfficerUid = prefill.probationOfficers[0]?.uid ?? null;

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateContact = (
    idx: number,
    key: keyof ContactDraft,
    value: string,
  ) => {
    setForm((prev) => {
      const next = prev.knownContacts.map((c, i) =>
        i === idx ? { ...c, [key]: value } : c,
      );
      return { ...prev, knownContacts: next };
    });
  };

  const addContact = () => {
    setForm((prev) =>
      prev.knownContacts.length < MAX_KNOWN_CONTACTS
        ? { ...prev, knownContacts: [...prev.knownContacts, blankContact()] }
        : prev,
    );
  };

  const removeContact = (idx: number) => {
    setForm((prev) => {
      if (prev.knownContacts.length <= MIN_KNOWN_CONTACTS) return prev;
      return {
        ...prev,
        knownContacts: prev.knownContacts.filter((_, i) => i !== idx),
      };
    });
  };

  const updatePhone = (
    key: "phone" | "employerPhone" | "supervisorPhone",
    raw: string,
  ) => {
    update(key, formatUsPhoneInput(raw));
  };

  const updateContactPhone = (idx: number, raw: string) => {
    updateContact(idx, "phone", formatUsPhoneInput(raw));
  };

  // ---- validation ----
  const errors: string[] = [];
  const phoneErrors: string[] = [];
  if (form.fullName.trim().length < 2) errors.push("Full legal name");
  const dobError = validateDob(form.dateOfBirth.trim());
  if (dobError) errors.push("Date of birth");
  if (!form.addressLine.trim()) errors.push("Address");
  if (!form.city.trim()) errors.push("City");
  if (!form.state.trim()) errors.push("State");
  if (!form.zipcode.trim()) errors.push("Zip code");
  if (!form.phone.trim()) {
    errors.push("Phone");
  } else if (!isValidUsPhone(form.phone)) {
    phoneErrors.push("Your phone");
  }
  if (!form.email.trim()) errors.push("Email");
  if (!isFilled(form.probationOfficerName)) errors.push("Probation officer name");
  if (!isFilled(form.employerName)) errors.push("Employer name");
  if (!isFilled(form.employerAddress)) errors.push("Employer address");
  // Employer phone, supervisor name, and supervisor phone are all optional.
  // If provided we still validate phone format so a typo doesn't sneak into
  // the lease PDF.
  if (isFilled(form.employerPhone) && !isValidUsPhone(form.employerPhone)) {
    phoneErrors.push("Employer phone");
  }
  if (isFilled(form.supervisorPhone) && !isValidUsPhone(form.supervisorPhone)) {
    phoneErrors.push("Supervisor phone");
  }

  // At least 1 contact must have BOTH name + phone.
  const validContactCount = form.knownContacts.filter(
    (c) => isFilled(c.name) && isFilled(c.phone),
  ).length;
  if (validContactCount === 0) errors.push("At least one known contact (name + phone)");
  form.knownContacts.forEach((c, idx) => {
    if (isFilled(c.phone) && !isValidUsPhone(c.phone)) {
      phoneErrors.push(`Contact ${idx + 1} phone`);
    }
  });

  if (!form.attestAccurate) errors.push("Attestation of accuracy");
  if (!form.agreeTerms) errors.push("Agreement to lease terms");

  const canSubmit = errors.length === 0 && phoneErrors.length === 0 && !submitting;

  async function handleSubmit() {
    setError(null);
    const sig = sigRef.current?.toPngBytes();
    if (!sig) {
      setError(
        signMode === "draw"
          ? "Please draw your signature in the box above."
          : "Please type your full name so we can render your signature.",
      );
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(leaseTemplateUrl);
      if (!res.ok) throw new Error(`Lease fetch failed: ${res.status}`);
      const pdfBytes = await res.arrayBuffer();

      // Filter and trim contacts. We accept any row with BOTH name+phone
      // filled; rows with only one field are dropped silently.
      const knownContacts: KnownContact[] = form.knownContacts
        .map((c) => ({
          name: c.name.trim(),
          phone: normalizeUsPhone(c.phone) ?? c.phone.trim(),
          relationship: c.relationship.trim() || null,
        }))
        .filter((c) => c.name.length > 0 && c.phone.length > 0)
        .slice(0, MAX_KNOWN_CONTACTS);

      const clientPhone = normalizeUsPhone(form.phone);
      if (!clientPhone) {
        setError(US_PHONE_INVALID_MSG);
        return;
      }

      const nowIso = new Date().toISOString();
      // Persist DOB as MM/DD/YYYY on the wire (matches the lease PDF stamp
      // format + the existing backend parseDobToTimestamp regex). The input
      // is ISO yyyy-mm-dd; we convert here.
      const dobIso = form.dateOfBirth.trim();
      const dobDisplay = isoDobToDisplay(dobIso);
      const exhibitA: ExhibitAPayload = {
        client: {
          fullName: form.fullName.trim(),
          dateOfBirth: dobDisplay,
          addressLine: form.addressLine.trim(),
          city: form.city.trim(),
          state: form.state.trim(),
          zipcode: form.zipcode.trim(),
          phone: clientPhone,
          email: form.email.trim(),
          caseNumber: form.caseNumber.trim() || null,
        },
        probationOfficer: {
          uid: linkedOfficerUid,
          name: form.probationOfficerName.trim(),
          // Officer phone is no longer collected. The lease and the user
          // record keep the field, but new enrollments leave it blank.
          phone: null,
        },
        work: {
          employerName: form.employerName.trim(),
          employerAddress: form.employerAddress.trim(),
          employerPhone: normalizeUsPhone(form.employerPhone) ?? "",
          supervisorName: form.supervisorName.trim(),
          supervisorPhone: normalizeUsPhone(form.supervisorPhone) ?? "",
        },
        knownContacts,
        attestation: {
          accurate: true,
          accurateAt: nowIso,
          agreeTerms: true,
          agreeTermsAt: nowIso,
        },
      };

      const signed = await signLeasePdf({
        pdfBytes,
        signaturePngBytes: sig,
        signerFullName: form.fullName.trim(),
        exhibitA: {
          client: exhibitA.client,
          probationOfficer: exhibitA.probationOfficer,
          work: exhibitA.work,
          knownContacts: exhibitA.knownContacts,
        },
      });
      onSigned({
        signedPdfBase64: signed.pdfBase64,
        signerFullName: form.fullName.trim(),
        exhibitA,
      });
    } catch (err) {
      console.error(err);
      setError(String((err as Error)?.message ?? err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">
          Your information
        </h2>
        <p className="text-sm text-slate-600 mt-1">
          Fill out the form below and sign at the bottom. On the next step
          you'll review the full signed lease before payment.
        </p>
      </div>

      <Section title="Your information">
        <Field label="Full legal name" colSpan="sm:col-span-2">
          <input
            type="text"
            autoComplete="name"
            value={form.fullName}
            onChange={(e) => update("fullName", e.target.value)}
            placeholder="Jane Q. Doe"
            className={inputClass}
          />
        </Field>
        <Field label="Date of birth">
          <input
            type="date"
            required
            min="1900-01-01"
            max={todayIso()}
            value={form.dateOfBirth}
            onChange={(e) => update("dateOfBirth", e.target.value)}
            className={inputClass}
          />
          {form.dateOfBirth && dobError ? (
            <p className="mt-1 text-xs text-red-600">{dobError}</p>
          ) : (
            <p className="mt-1 text-xs text-slate-500">
              Format: MM/DD/YYYY (your device may show a calendar picker).
            </p>
          )}
        </Field>
        <Field label="Phone">
          <input
            type="tel"
            autoComplete="tel"
            value={form.phone}
            onChange={(e) => updatePhone("phone", e.target.value)}
            placeholder="(915) 555-0123"
            className={inputClass}
          />
        </Field>
        <Field label="Street address" colSpan="sm:col-span-2">
          <input
            type="text"
            autoComplete="street-address"
            value={form.addressLine}
            onChange={(e) => update("addressLine", e.target.value)}
            placeholder="123 Main St, Apt 4B"
            className={inputClass}
          />
        </Field>
        <Field label="City">
          <input
            type="text"
            autoComplete="address-level2"
            value={form.city}
            onChange={(e) => update("city", e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="State">
          <input
            type="text"
            autoComplete="address-level1"
            value={form.state}
            onChange={(e) => update("state", e.target.value)}
            maxLength={2}
            placeholder="TX"
            className={`${inputClass} uppercase`}
          />
        </Field>
        <Field label="Zip code">
          <input
            type="text"
            inputMode="numeric"
            autoComplete="postal-code"
            value={form.zipcode}
            onChange={(e) => update("zipcode", e.target.value)}
            maxLength={10}
            className={inputClass}
          />
        </Field>
        <Field label="Email" colSpan="sm:col-span-2">
          <input
            type="email"
            autoComplete="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field
          label="Court case number (if known)"
          help="Optional — leave blank if you don't have it yet."
          colSpan="sm:col-span-2"
        >
          <input
            type="text"
            autoComplete="off"
            value={form.caseNumber}
            onChange={(e) => update("caseNumber", e.target.value)}
            placeholder="e.g. 2024-CR-12345"
            className={inputClass}
          />
        </Field>
      </Section>

      <Section title="Pretrial / Probation officer">
        <Field label="Officer name" colSpan="sm:col-span-2">
          <input
            type="text"
            value={form.probationOfficerName}
            onChange={(e) => update("probationOfficerName", e.target.value)}
            placeholder="Ofc. M. Garcia"
            className={inputClass}
          />
        </Field>
      </Section>

      <Section title="Work references">
        <Field label="Employer" colSpan="sm:col-span-2">
          <input
            type="text"
            autoComplete="organization"
            value={form.employerName}
            onChange={(e) => update("employerName", e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Work address" colSpan="sm:col-span-2">
          <input
            type="text"
            value={form.employerAddress}
            onChange={(e) => update("employerAddress", e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Work phone (optional)">
          <input
            type="tel"
            value={form.employerPhone}
            onChange={(e) => updatePhone("employerPhone", e.target.value)}
            placeholder="(915) 555-0123"
            className={inputClass}
          />
        </Field>
        <Field label="Supervisor name (optional)">
          <input
            type="text"
            value={form.supervisorName}
            onChange={(e) => update("supervisorName", e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Supervisor phone (optional)" colSpan="sm:col-span-2">
          <input
            type="tel"
            value={form.supervisorPhone}
            onChange={(e) => updatePhone("supervisorPhone", e.target.value)}
            placeholder="(915) 555-0123"
            className={inputClass}
          />
        </Field>
      </Section>

      <Section title="Known contacts">
        <div className="sm:col-span-2 -mt-2 mb-1 text-xs text-slate-500">
          We won't contact them unless you fail to return the device.
        </div>
        {form.knownContacts.map((contact, idx) => (
          <div
            key={idx}
            className="sm:col-span-2 rounded-lg border border-slate-200 bg-slate-50 p-3"
          >
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-medium text-slate-700">
                Contact {idx + 1}
              </span>
              {form.knownContacts.length > MIN_KNOWN_CONTACTS ? (
                <button
                  type="button"
                  onClick={() => removeContact(idx)}
                  className="text-xs text-slate-500 hover:text-red-600"
                >
                  Remove
                </button>
              ) : null}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Name" colSpan="sm:col-span-2">
                <input
                  type="text"
                  value={contact.name}
                  onChange={(e) => updateContact(idx, "name", e.target.value)}
                  className={inputClass}
                />
              </Field>
              <Field label="Phone">
                <input
                  type="tel"
                  value={contact.phone}
                  onChange={(e) => updateContactPhone(idx, e.target.value)}
                  placeholder="(915) 555-0123"
                  className={inputClass}
                />
              </Field>
              <Field label="Relationship">
                <select
                  value={contact.relationship}
                  onChange={(e) =>
                    updateContact(idx, "relationship", e.target.value)
                  }
                  className={inputClass}
                >
                  {RELATIONSHIP_OPTIONS.map((opt) => (
                    <option key={opt.value || "unset"} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </div>
        ))}
        {form.knownContacts.length < MAX_KNOWN_CONTACTS ? (
          <div className="sm:col-span-2">
            <button
              type="button"
              onClick={addContact}
              className="text-sm font-medium text-brand-600 hover:text-brand-700"
            >
              + Add another contact
            </button>
          </div>
        ) : null}
      </Section>

      <Section title="Sign the lease">
        <div className="sm:col-span-2 space-y-3">
          <div
            role="tablist"
            aria-label="Signature mode"
            className="inline-flex rounded-lg bg-slate-100 p-1"
          >
            <ModeTab
              active={signMode === "draw"}
              onClick={() => setSignMode("draw")}
            >
              Draw
            </ModeTab>
            <ModeTab
              active={signMode === "type"}
              onClick={() => setSignMode("type")}
            >
              Type
            </ModeTab>
          </div>

          {signMode === "draw" ? (
            <>
              <div className="flex items-baseline justify-between">
                <span className="text-xs text-slate-500">
                  Sign with your finger, mouse, or stylus.
                </span>
                <button
                  type="button"
                  onClick={() => {
                    sigRef.current?.clear();
                    forceTick((n) => n + 1);
                  }}
                  className="text-xs text-brand-600 hover:text-brand-700"
                >
                  Clear
                </button>
              </div>
              <SignatureCanvas ref={sigRef} />
            </>
          ) : (
            <>
              <div className="text-xs text-slate-500">
                We'll render your typed name in a cursive script as your
                signature.
              </div>
              <TypedSignature ref={sigRef} name={form.fullName} />
            </>
          )}

          <p className="text-sm text-slate-600">
            Date: <span className="font-medium text-slate-900">{today}</span>
          </p>
        </div>

        <div className="sm:col-span-2 space-y-3">
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
            <p className="font-medium text-amber-900">Accuracy matters</p>
            <p className="mt-1 text-amber-800 leading-relaxed">
              Please provide information that is complete and accurate to the
              best of your knowledge. Incorrect, outdated, or incomplete details
              may delay enrollment, affect compliance reporting, or create
              issues with your monitoring program.
            </p>
          </div>

          <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={form.attestAccurate}
              onChange={(e) => update("attestAccurate", e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-500 focus:ring-brand-500"
            />
            <span>
              I confirm that all information I have provided is true and
              accurate to the best of my knowledge.
            </span>
          </label>

          <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={form.agreeTerms}
              onChange={(e) => update("agreeTerms", e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-500 focus:ring-brand-500"
            />
            <span>
              I have read and agree to the lease agreement above, and I
              understand this constitutes my electronic signature with the same
              legal effect as a handwritten signature.
            </span>
          </label>
        </div>
      </Section>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {phoneErrors.length > 0 ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800">
          {US_PHONE_INVALID_MSG} Please check: {phoneErrors.join(", ")}.
        </div>
      ) : null}

      {errors.length > 0 ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          Almost there — still need: {errors.join(", ")}.
        </div>
      ) : null}

      <button
        type="button"
        disabled={!canSubmit}
        onClick={handleSubmit}
        className="w-full rounded-lg bg-brand-500 px-4 py-3 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-brand-600 transition"
      >
        {submitting ? "Preparing…" : "Sign and preview lease"}
      </button>
    </div>
  );
}

// ---------- small layout primitives ----------

const inputClass =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-base focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none";

function Section({
  title,
  headerRight,
  children,
}: {
  title: string;
  headerRight?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <header className="mb-4 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        {headerRight}
      </header>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
    </section>
  );
}

function Field({
  label,
  help,
  colSpan,
  children,
}: {
  label: string;
  help?: string;
  colSpan?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={colSpan ?? ""}>
      <label className="block text-xs font-medium text-slate-700 mb-1">
        {label}
      </label>
      {children}
      {help ? <p className="mt-1 text-xs text-slate-500">{help}</p> : null}
    </div>
  );
}

function ModeTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={
        "px-4 py-1.5 text-sm font-medium rounded-md transition " +
        (active
          ? "bg-white text-slate-900 shadow-sm"
          : "text-slate-600 hover:text-slate-900")
      }
    >
      {children}
    </button>
  );
}
